import React, { useState, useEffect } from 'react';
import styles from './AddDollarAccountForm.module.css';

interface AddDollarAccountFormProps {
  onSubmit: (formData: DollarAccountFormData) => void;
  onCancel: () => void;
  initialData?: Partial<DollarAccountFormData>;
}

export interface DollarAccountFormData {
  accountName: string;
  accountType: string;
  bankName: string;
  holderName: string;
  accountNo: string;
  ifscSwiftCode: string;
  openingBalance: string;
  openingDate: string;
  currentBal: string;
  status: string;
}

const AddDollarAccountForm: React.FC<AddDollarAccountFormProps> = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState<DollarAccountFormData>({
    accountName: '',
    accountType: '1',
    bankName: '',
    holderName: '',
    accountNo: '',
    ifscSwiftCode: '',
    openingBalance: '0',
    openingDate: new Date().toISOString().split('T')[0],
    currentBal: '0',
    status: 'Active',
  });

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Check if account type is Bank Account
  const isBankAccount = formData.accountType === '1';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="accountName" className={styles.label}>
            Account Name <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            id="accountName"
            name="accountName"
            value={formData.accountName}
            onChange={handleChange}
            className={styles.input}
            required
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="accountType" className={styles.label}>
            Account Type <span className={styles.required}>*</span>
          </label>
          <select
            id="accountType"
            name="accountType"
            value={formData.accountType}
            onChange={handleChange}
            className={styles.select}
            required
          >
            <option value="1">Bank Account</option>
            <option value="2">Wallet</option>
            <option value="3">Cash</option>
          </select>
        </div>
      </div>

      <div className={styles.row} style={{ display: isBankAccount ? 'grid' : 'none' }}>
        <div className={styles.field}>
          <label htmlFor="bankName" className={styles.label}>
            Bank Name
          </label>
          <input
            type="text"
            id="bankName"
            name="bankName"
            value={formData.bankName}
            onChange={handleChange}
            className={styles.input}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="holderName" className={styles.label}>
            Holder Name
          </label>
          <input
            type="text"
            id="holderName"
            name="holderName"
            value={formData.holderName}
            onChange={handleChange}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.row} style={{ display: isBankAccount ? 'grid' : 'none' }}>
        <div className={styles.field}>
          <label htmlFor="accountNo" className={styles.label}>
            Account No.
          </label>
          <input
            type="text"
            id="accountNo"
            name="accountNo"
            value={formData.accountNo}
            onChange={handleChange}
            className={styles.input}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="ifscSwiftCode" className={styles.label}>
            IFSC/SWIFT Code
          </label>
          <input
            type="text"
            id="ifscSwiftCode"
            name="ifscSwiftCode"
            value={formData.ifscSwiftCode}
            onChange={handleChange}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="openingBalance" className={styles.label}>
            Opening Balance (₹)
          </label>
          <input
            type="number"
            id="openingBalance"
            name="openingBalance"
            value={formData.openingBalance}
            onChange={handleChange}
            className={styles.input}
            step="0.01"
            min="0"
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="openingDate" className={styles.label}>
            Opening Date
          </label>
          <input
            type="date"
            id="openingDate"
            name="openingDate"
            value={formData.openingDate}
            onChange={handleChange}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.fullWidthField}>
        <label htmlFor="status" className={styles.label}>
          Status
        </label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          className={styles.select}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div className={styles.actions}>
        <button type="button" onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
        <button type="submit" className={styles.submitButton}>
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default AddDollarAccountForm;
